import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  Row,
  Col,
  Divider,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  UploadOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  BankOutlined,
  SafetyOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { API_URL } from "../../variables/constants";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Option } = Select;
const { Text } = Typography;

const TYPE_LABELS = {
  ASSURANCE: { label: "Assurance", color: "blue" },
  PARTENAIRE: { label: "Partenaire", color: "green" },
  CONCESSIONNAIRE: { label: "Concessionnaire", color: "orange" },
};

const AddMembres = ({ show, handleClose, editMode, selectedMembre }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [fileImage, setFileImage] = useState(null);

  useEffect(() => {
    if (!show) return;

    if (editMode && selectedMembre?.organismeId) {
      setFetchLoading(true);
      axiosInstance
        .get(`/api/organisme/${selectedMembre.organismeId}`)
        .then(({ data }) => {
          form.setFieldsValue({
            raisonSociale: data.raisonSociale,
            siegeSocial: data.siegeSocial,
            code: data.code,
            dateCreation: data.dateCreation ? dayjs(data.dateCreation) : null,
            email: data.email,
            telephone1: data.contact?.[0] || "",
            telephone2: data.contact?.[1] || "",
            typeOrganisme: data.typeOrganisme,
            responsable: data.responsable,
            otpConnexion: data.otpConnexion || "NONE",
            domaine: data.domaine,
            website: data.website,
          });
          if (data.logo) {
            setImagePreviewUrl(
              `${API_URL}/api/organisme/logo/${data.logo.split("/")[1]}`
            );
          }
        })
        .catch(() =>
          toast.error("Erreur lors du chargement des informations du membre.")
        )
        .finally(() => setFetchLoading(false));
    } else {
      form.resetFields();
      setImagePreviewUrl(null);
      setFileImage(null);
    }
  }, [show, editMode, selectedMembre, form]);

  const handleImageChange = (info) => {
    const file = info.file.originFileObj ?? info.file;
    setFileImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreviewUrl(reader.result);
    if (file) reader.readAsDataURL(file);
    return false;
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    const payload = {
      ...selectedMembre,
      ...values,
      contact: [values.telephone1, values.telephone2].filter(Boolean),
      dateCreation: values.dateCreation?.format("YYYY-MM-DD"),
      telephone1: undefined,
      telephone2: undefined,
      active: true,
    };

    try {
      if (editMode && selectedMembre?.organismeId) {
        await axiosInstance.put("/api/organisme", payload);
        toast.success("Membre mis à jour avec succès !");
      } else {
        await axiosInstance.post("/api/organisme", payload);
        toast.success("Membre créé avec succès !");
      }

      if (fileImage) {
        const formData = new FormData();
        const ext = fileImage.name.split(".").pop();
        const filename = `Logo_${values.raisonSociale.split(" ").join("-")}.${ext}`;
        formData.append("fichier", fileImage, filename);
        await axiosMultiPartDataInstance.post("/api/organisme/logo", formData);
      }

      handleClose();
      window.location.reload();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Erreur lors de l'enregistrement du membre.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={show}
      onCancel={handleClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BankOutlined style={{ fontSize: 18, color: "#1677ff" }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {editMode ? "Modifier le membre" : "Nouveau membre du Pool"}
          </span>
          {editMode && selectedMembre?.typeOrganisme && (
            <Tag color={TYPE_LABELS[selectedMembre.typeOrganisme]?.color}>
              {TYPE_LABELS[selectedMembre.typeOrganisme]?.label}
            </Tag>
          )}
        </div>
      }
      footer={null}
      width={760}
      destroyOnClose
      centered
      styles={{ body: { paddingTop: 8 } }}
    >
      <Spin spinning={fetchLoading} tip="Chargement...">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          size="middle"
        >
          {/* ── Identification ─────────────────────────────────── */}
          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
              IDENTIFICATION
            </Text>
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={14}>
              <Form.Item
                name="raisonSociale"
                label="Dénomination sociale"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <Input
                  prefix={<BankOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="Ex. ALLIANZ TOGO"
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={5}>
              <Form.Item
                name="code"
                label="Code"
                rules={[{ required: true, message: "Requis" }]}
              >
                <Input placeholder="Ex. 07" style={{ textTransform: "uppercase" }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={5}>
              <Form.Item
                name="typeOrganisme"
                label="Type"
                rules={[{ required: true, message: "Requis" }]}
              >
                <Select placeholder="Choisir">
                  <Option value="ASSURANCE">Assurance</Option>
                  <Option value="PARTENAIRE">Partenaire</Option>
                  <Option value="CONCESSIONNAIRE">Concessionnaire</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={12}>
              <Form.Item
                name="dateCreation"
                label="Date de création"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="JJ/MM/AAAA"
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12}>
              <Form.Item name="responsable" label="Responsable">
                <Input
                  prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="Nom du responsable"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Coordonnées ────────────────────────────────────── */}
          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
              COORDONNÉES
            </Text>
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="siegeSocial"
                label="Siège social"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <Input
                  prefix={<HomeOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="Ex. LOMÉ"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Adresse email"
                rules={[
                  { required: true, message: "Champ requis" },
                  { type: "email", message: "Format email invalide" },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="contact@organisme.tg"
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12}>
              <Form.Item
                name="telephone1"
                label="Téléphone principal"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="+228 XX XX XX XX"
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12}>
              <Form.Item name="telephone2" label="Téléphone secondaire">
                <Input
                  prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="+228 XX XX XX XX"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Configuration ──────────────────────────────────── */}
          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
              CONFIGURATION
            </Text>
          </Divider>
          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Form.Item name="otpConnexion" label="Authentification OTP">
                <Select defaultValue="NONE">
                  <Option value="NONE">
                    <SafetyOutlined /> Désactivée
                  </Option>
                  <Option value="SMS">SMS</Option>
                  <Option value="EMAIL">Email</Option>
                  <Option value="WHATSAPP">WhatsApp</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="domaine" label="Nom de domaine">
                <Input
                  prefix={<GlobalOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="organisme.tg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="website" label="Site web">
                <Input
                  prefix={<GlobalOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="https://www.organisme.tg"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Logo ───────────────────────────────────────────── */}
          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
              LOGO
            </Text>
          </Divider>
          <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
            {imagePreviewUrl && (
              <Col flex="80px">
                <div
                  style={{
                    width: 72,
                    height: 72,
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#fafafa",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={imagePreviewUrl}
                    alt="Logo"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                </div>
              </Col>
            )}
            <Col flex="auto">
              <Upload
                accept="image/png,image/jpeg"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleImageChange}
              >
                <Button icon={<UploadOutlined />}>
                  {imagePreviewUrl ? "Changer le logo" : "Téléverser un logo"}
                </Button>
              </Upload>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                PNG ou JPEG · Fond transparent recommandé
              </div>
            </Col>
          </Row>

          {/* ── Actions ────────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 24,
              paddingTop: 16,
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Button onClick={handleClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editMode ? "Enregistrer les modifications" : "Créer le membre"}
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddMembres;
